import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FaMicrophone, FaMicrophoneSlash, FaVolumeUp, FaStop, FaRocket, FaFilter } from 'react-icons/fa';
import ChatSearchWizard from './ChatSearchWizard';
import ChatFilterWizard from './ChatFilterWizard';
import langflowService from '../services/langflowService';
import { getDiscoverChatContext } from '../shared/discoverChatContext';
import './ChatBotModal.css';

const getCurrentRoutePath = () => {
  if (typeof window === 'undefined') return '';
  const hash = window.location.hash || '';
  return hash ? hash.replace(/^#/, '') : window.location.pathname;
};

const GLOBAL_SUGGESTIONS = [
  'What is a sequence search?',
  'How do I read alignment results?',
  'What is a Variation Report?',
];

const DISCOVER_SUGGESTIONS = [
  'What are the features of Discover Browser?',
  'How do I filter Discover results?',
  'How do I customize columns in Discover?',
];

const buildRunAgentUrl = () => {
  return `${process.env.REACT_APP_BASE_URL}jnrb/run-agent`;
};

const LOADING_MESSAGES = [
  'Searching patent sequences...',
  'Scanning prior art databases...',
  'Analyzing sequence alignments...',
  'Querying IP sequence records...',
  'Cross-referencing patent families...',
  'Checking freedom-to-operate data...',
  'Mining sequence homology results...',
  'Reviewing patentability criteria...',
];

const GQ_TIPS = [
  { icon: '🔍', title: 'Sequence Search', body: 'Pick the right strategy: GenePAST for short sequences, BLAST for longer sequences, and MOTIF for variable sequences.' },
  { icon: '📊', title: 'Alignment Results', body: 'Alignment scores and identity % tell you how closely your query matches a patent sequence — higher identity means closer prior art.' },
  { icon: '🌿', title: 'Variation Reports', body: 'Use Variation Reports to explore minor sequence variants (SNPs, indels) across entire patent families in one view.' },
  { icon: '🔬', title: 'Filter & Refine', body: 'Narrow results by organism, patent authority (USPTO, EPO, WIPO), sequence type, or date range for faster analysis.' },
  { icon: '💾', title: 'Saved Searches', body: 'Save any search to revisit results later without re-running the query — great for tracking evolving patent landscapes.' },
];

const TIPS_SEEN_KEY = 'gq_assist_tips_seen';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

const ChatBotModal = ({ isOpen, onClose, initialMessage }) => {
  const isDiscoverRoute = getCurrentRoutePath().startsWith('/discover');
  const suggestions = isDiscoverRoute ? DISCOVER_SUGGESTIONS : GLOBAL_SUGGESTIONS;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const initialMessageRef = useRef(null);

  // When an initialMessage is passed (from text selection), populate the input
  useEffect(() => {
    if (isOpen && initialMessage && initialMessage !== initialMessageRef.current) {
      initialMessageRef.current = initialMessage;
      setInput(initialMessage);
    }
  }, [isOpen, initialMessage]);
  const [showWizard, setShowWizard] = useState(0);
  const [showFilterWizard, setShowFilterWizard] = useState(0);
  const [loadingMsg, setLoadingMsg] = useState(LOADING_MESSAGES[0]);
  const loadingMsgRef = useRef(null);
  const [showTips, setShowTips] = useState(() => !localStorage.getItem(TIPS_SEEN_KEY));
  const [tipIndex, setTipIndex] = useState(0);

  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Voice assistant state
  const [listening, setListening] = useState(false);
  const [speakingMsgIdx, setSpeakingMsgIdx] = useState(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (loading) {
      let idx = 0;
      loadingMsgRef.current = setInterval(() => {
        idx = (idx + 1) % LOADING_MESSAGES.length;
        setLoadingMsg(LOADING_MESSAGES[idx]);
      }, 2000);
    } else {
      clearInterval(loadingMsgRef.current);
      setLoadingMsg(LOADING_MESSAGES[0]);
    }
    return () => clearInterval(loadingMsgRef.current);
  }, [loading]);
  // Voice recognition handlers
  const startListening = () => {
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.continuous = false;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(prev => prev ? prev + ' ' + transcript : transcript);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setListening(false);
  };

  const toggleListening = () => {
    if (listening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Text-to-speech for bot messages
  const speakMessage = (text, idx) => {
    const synth = synthRef.current;
    if (!synth) return;

    // If already speaking this message, stop
    if (speakingMsgIdx === idx) {
      synth.cancel();
      setSpeakingMsgIdx(null);
      return;
    }

    synth.cancel();
    // Strip HTML/markdown for clean speech
    const cleanText = text.replace(/<[^>]*>/g, '').replace(/[*_#`~]/g, '');
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 1;
    utterance.onend = () => setSpeakingMsgIdx(null);
    utterance.onerror = () => setSpeakingMsgIdx(null);
    setSpeakingMsgIdx(idx);
    synth.speak(utterance);
  };

  // Stop speech when modal closes
  useEffect(() => {
    if (!isOpen) {
      stopListening();
      synthRef.current?.cancel();
      setSpeakingMsgIdx(null);
    }
  }, [isOpen]);

  const [error, setError] = useState(null);
  const endpointDebugLogged = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && !endpointDebugLogged.current) {
      console.debug('[ChatBotModal] run-agent endpoint:', buildRunAgentUrl());
      endpointDebugLogged.current = true;
    }
  }, []);

  const handleSend = async () => {
    if (!input.trim()) return;
    const userInput = input.trim();
    setMessages(prev => [...prev, { sender: 'user', text: userInput }]);
    setLoading(true);
    setError(null);

    try {
      let botText;
      if (isDiscoverRoute) {
        const ctx = getDiscoverChatContext();
        if (!ctx || ctx.aiDataLoading || !ctx.aiPatentData) {
          botText = "Workflow data is still loading. Please wait a moment and try again.";
        } else {
          botText = await langflowService.sendMessage(userInput, {
            resInfo: ctx.resInfo,
            allResults: ctx.aiPatentData,
          });
        }
      } else {
        const requestBody = {
          payload: userInput,
          request_id: `ui-${Date.now()}`,
          flow_id: "8ada220c-beff-424e-abb6-6561a9b53e26",
          session_id: "",
        };
        const response = await fetch(buildRunAgentUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });
        if (!response.ok) throw new Error(`Request failed (${response.status})`);
        const data = await response.json();
        botText = data?.outputs?.[0]?.outputs?.[0]?.results?.message?.data?.text || "Sorry, I couldn't generate a response.";
      }
      setMessages(prev => [...prev, { sender: 'bot', text: botText || 'No response received.' }]);
    } catch (err) {
      setMessages(prev => [...prev, { sender: 'bot', text: 'Error: ' + err.message }]);
      setError(err.message);
    } finally {
      setLoading(false);
      setInput('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chatbot-modal-overlay">
      <div className="chatbot-modal">
        <div className="chatbot-header">
          <span className="chatbot-header-title">🧬 GQ Assist</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {messages.length > 0 && (
              <button className="chatbot-clear" onClick={() => setMessages([])} title="Clear chat">🗑 Clear</button>
            )}
            <button className="chatbot-close" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="chatbot-messages">
          {showTips && messages.length === 0 && !loading && (
            <div className="chatbot-tips-panel">
              <div className="chatbot-tips-header">
                <span className="chatbot-tips-label">💡 Quick Tips</span>
                <span className="chatbot-tips-counter">{tipIndex + 1} / {GQ_TIPS.length}</span>
              </div>
              <div className="chatbot-tip-card">
                <div className="chatbot-tip-icon">{GQ_TIPS[tipIndex].icon}</div>
                <div className="chatbot-tip-body">
                  <div className="chatbot-tip-title">{GQ_TIPS[tipIndex].title}</div>
                  <div className="chatbot-tip-text">{GQ_TIPS[tipIndex].body}</div>
                </div>
              </div>
              <div className="chatbot-tips-nav">
                <button
                  className="chatbot-tips-arrow"
                  onClick={() => setTipIndex(i => (i - 1 + GQ_TIPS.length) % GQ_TIPS.length)}
                  title="Previous tip"
                >‹</button>
                <div className="chatbot-tips-dots">
                  {GQ_TIPS.map((_, i) => (
                    <span
                      key={i}
                      className={`chatbot-tips-dot${i === tipIndex ? ' active' : ''}`}
                      onClick={() => setTipIndex(i)}
                    />
                  ))}
                </div>
                <button
                  className="chatbot-tips-arrow"
                  onClick={() => setTipIndex(i => (i + 1) % GQ_TIPS.length)}
                  title="Next tip"
                >›</button>
              </div>
              <button
                className="chatbot-tips-dismiss"
                onClick={() => { localStorage.setItem(TIPS_SEEN_KEY, '1'); setShowTips(false); }}
              >Got it, thanks!</button>
            </div>
          )}
          {!showTips && messages.length === 0 && !loading && !showWizard && !showFilterWizard && (
            <div className="chatbot-welcome">
              <div className="chatbot-welcome-icon">🧬</div>
              <div className="chatbot-welcome-title">Hi, I'm GQ Assist!</div>
              <div className="chatbot-welcome-sub">Ask me anything about GenomeQuest — sequence searching, patent workflows, alignment results, and more.</div>
              <button className="chatbot-launch-search-btn" onClick={() => setShowWizard(prev => prev + 1)}>
                <FaRocket /> Launch a Search
              </button>
              {isDiscoverRoute && (
                <button className="chatbot-create-filter-btn" onClick={() => setShowFilterWizard(prev => prev + 1)}>
                  <FaFilter /> Create Custom Filter
                </button>
              )}
              <div className="chatbot-welcome-suggestions">
                {suggestions.map(q => (
                  <button key={q} className="chatbot-suggestion" onClick={() => setInput(q)}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!!showWizard && (
            <ChatSearchWizard
              key={showWizard}
              onClose={() => setShowWizard(0)}
              onSearchSubmitted={() => {
                setMessages(prev => [...prev, { sender: 'bot', text: 'Your search has been submitted. Redirecting to the home page to view results...' }]);
                setShowWizard(0);
              }}
            />
          )}
          {showFilterWizard && (
            <ChatFilterWizard
              key={showFilterWizard}
              onClose={() => setShowFilterWizard(0)}
            />
          )}
          {messages.map((msg, idx) => (
            <div key={idx} className={`chatbot-message chatbot-message-${msg.sender}`}>
              {msg.sender === 'bot' ? (
                <>
                  <div><ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text || ''}</ReactMarkdown></div>
                  {synthRef.current && (
                    <button
                      className={`chatbot-speak-btn${speakingMsgIdx === idx ? ' speaking' : ''}`}
                      onClick={() => speakMessage(msg.text, idx)}
                      title={speakingMsgIdx === idx ? 'Stop reading' : 'Read aloud'}
                    >
                      {speakingMsgIdx === idx ? <FaStop /> : <FaVolumeUp />}
                    </button>
                  )}
                </>
              ) : msg.text}
            </div>
          ))}
          {loading && (
            <div className="chatbot-loading-bubble" ref={messagesEndRef}>
              <div className="chatbot-loading-dots">
                <span /><span /><span />
              </div>
              {loadingMsg}
            </div>
          )}
          {!loading && <div ref={messagesEndRef} />}
        </div>
        {!showWizard && !showFilterWizard && (
        <div className="chatbot-input-area">
        <div className="chatbot-input-row">
          <button
            className="chatbot-input-launch-btn"
            onClick={() => setShowWizard(prev => prev + 1)}
            disabled={loading}
            title="Launch a search"
          >
            <FaRocket />
          </button>
          <textarea
            value={input}
            onChange={e => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            placeholder={listening ? 'Listening...' : 'Type your question...'}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            disabled={loading}
            rows={1}
          />
          {SpeechRecognition && (
            <button
              className={`chatbot-mic-btn${listening ? ' listening' : ''}`}
              onClick={toggleListening}
              disabled={loading}
              title={listening ? 'Stop listening' : 'Voice input'}
            >
              {listening ? <FaMicrophoneSlash /> : <FaMicrophone />}
            </button>
          )}
          <button onClick={handleSend} disabled={loading}>Send</button>
        </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default ChatBotModal;
